import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  inject
} from '@angular/core'
import { CommonModule, NgOptimizedImage } from '@angular/common'

import { toSignal } from '@angular/core/rxjs-interop'
import { Router, RouterLink } from '@angular/router'
import { CurrentUserFacade } from '@navix/current-user/domain'
import {
  FormGroup,
  FormControl,
  Validators,
  ReactiveFormsModule
} from '@angular/forms'
import { DynamicValidatorMessageDirective } from '@navix/forms'
import { map } from 'rxjs'
import { LoadingStatuses } from '@navix/shared/loading'
import { AuthService } from '@auth0/auth0-angular'
@Component({
  selector: 'navix-login',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterLink,
    ReactiveFormsModule,
    DynamicValidatorMessageDirective
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent {
  @HostBinding('class') readonly class =
    'h-screen flex flex-col justify-center items-center'

  private readonly router = inject(Router)
  private readonly currentUserFacade = inject(CurrentUserFacade)
  private readonly authService = inject(AuthService)

  loading = toSignal(
    this.currentUserFacade.loading$.pipe(
      map(loading => loading.login),
      map(loading => loading.status === LoadingStatuses.InProgress)
    )
  )

  authServiceError = toSignal(this.authService.error$)

  showLoginErrorMessage = computed(() => {
    if (this.authServiceError()?.message.includes('inactive')) return true

    return false
  })

  form = new FormGroup({
    email: new FormControl<string>(
      '',
      Validators.compose([
        Validators.required,
        Validators.maxLength(320),
        Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$')
      ])
    )
  })

  //Todo(ladaj): temporal until we upload the logo to the cdn
  protected mainLargeLogo = `data:image/png;base64,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`

  onRedirectToForgotPassword(): void {
    this.router.navigate(['/forgot-password'])
  }

  onSubmit() {
    if (this.form.invalid || this.form.value.email == undefined) return
    this.currentUserFacade.login(this.form.value.email)
  }
}
